import { useEffect, useState } from 'react'
import { useRequest } from 'ahooks'

import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { AppAuditLog, getAppAuditLogs } from '$services/api'
import { useApiListingParams } from '$hooks/actions'

import ScrollPagination from '$components/ScrollPagination'
import Loading from '$components/Loading'
import EmptyData from '$components/EmptyData'
import TimelineItem from '$components/TimelineItem'

import { flexColumn } from '$styles/common.css'
import {
  table,
  tableBodyCell,
  tableContainer,
  tableHead,
  tableHeadCell
} from './styles.css'
import {
  tableBody,
  tableContainerInner,
  tableRow
} from '@genie-fintech/ui/style/element'

const PER_PAGE = 15

const newActions = ['updated', 'created', 'add']

const getDescription = (item: AppAuditLog) => {
  const result = [item.description]

  const isNewAction = newActions.some(action => item.action.includes(action))

  const name = isNewAction
    ? item.log_details?.new_value?.name
    : item.log_details?.old_value?.name

  if (name) result.push(`(${name})`)

  return result.join(' ')
}

const DrawerContent = () => {
  const [auditLogs, setAuditLogs] = useState<AppAuditLog[]>([])

  const { route } = useRouteSummary()
  const { appId } = route.params

  const { loading, runAsync, data } = useRequest(getAppAuditLogs, {
    manual: true
  })

  const { meta } = data ?? {}

  const { pagerProps } = useApiListingParams({
    perPage: PER_PAGE,
    total: meta?.total
  })

  const { currentPage, jump } = pagerProps

  useEffect(() => {
    if (!appId) return

    runAsync({
      appId,
      page: currentPage,
      per_page: PER_PAGE
    }).then(({ data }) => {
      setAuditLogs(prev => (currentPage === 1 ? data : [...prev, ...data]))
    })
  }, [appId, currentPage, runAsync])

  const initialLoading = currentPage === 1 && loading && !auditLogs.length

  const hasData = !!auditLogs.length

  const isEmpty = !auditLogs.length && !loading

  return (
    <>
      {initialLoading && <Loading />}

      {isEmpty && <EmptyData type="audit log" />}

      {hasData && (
        <article className={flexColumn} style={{ flex: 1 }}>
          {auditLogs.map((item, index) => {
            const { old_value = {}, new_value = {} } = { ...item.log_details }

            const showTable = !!old_value && !!new_value

            return (
              <TimelineItem
                key={index}
                date={item.timestamp}
                title={getDescription(item)}
                email={item.performed_by.email}
                role={item.performed_by.role}
              >
                {showTable && (
                  <section className={tableContainer}>
                    <main className={tableContainerInner}>
                      <table className={table}>
                        <thead className={tableHead}>
                          <tr className={tableRow}>
                            <th className={tableHeadCell}>Title</th>
                            <th className={tableHeadCell}>Before</th>
                            <th className={tableHeadCell}>Updated Value</th>
                          </tr>
                        </thead>
                        <tbody className={tableBody}>
                          {Object.entries(new_value).map(([key, value]) => {
                            const newValue = value || null
                            const oldValue = old_value[key as never] || null

                            if (newValue === oldValue) return null

                            const title = key.split('_').join(' ')

                            const renderValue = (value: unknown) => {
                              if (value) {
                                if (Array.isArray(value)) {
                                  return (
                                    <ul className="flex flex-col gap-2">
                                      {value.map((item, index) => (
                                        <li key={index}>{renderValue(item)}</li>
                                      ))}
                                    </ul>
                                  )
                                }

                                if (typeof value === 'object') {
                                  return (
                                    <ul className="flex flex-col gap-2">
                                      {Object.entries(value).map(
                                        ([key, val], index) => (
                                          <li key={index}>
                                            {key.toUpperCase()}:{' '}
                                            {renderValue(val)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )
                                }

                                return JSON.stringify(value).replace(/"/g, '')
                              }
                              return 'N/A'
                            }

                            return (
                              <tr key={key} className={tableRow}>
                                <td
                                  className={tableBodyCell}
                                  style={{ textTransform: 'uppercase' }}
                                >
                                  {title}
                                </td>
                                <td className={tableBodyCell}>
                                  {renderValue(oldValue)}
                                </td>
                                <td className={tableBodyCell}>
                                  {renderValue(newValue)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </main>
                  </section>
                )}
              </TimelineItem>
            )
          })}
        </article>
      )}

      <ScrollPagination loading={loading} onTrigger={jump} meta={meta} />
    </>
  )
}

export default DrawerContent
